/**
 * Hero — eyebrow, headline, subhead, primary/secondary CTAs, proof chips and a
 * pure-CSS/SVG product-frame visual (a mocked "browser window" dashboard with
 * decorative gradient blobs). No 100vh lock: the section sizes to its content.
 */

import { HERO } from '@/routes/landing/landing-data';
import { Icon, PrimaryCta, SecondaryCta, useLandingCta } from './shared';

export function Hero() {
  const { signin, signup } = useLandingCta();

  return (
    <section id="top" className="relative overflow-hidden">
      {/* Decorative gradient wash + blobs (behind content, non-interactive). */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.07] via-transparent to-transparent" />
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -right-16 top-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pb-28 lg:pt-24">
        {/* Copy column */}
        <div className="um-animate-page">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary shadow-elevation-1">
            <Icon name="sparkles" className="h-4 w-4" strokeWidth={2} />
            {HERO.eyebrow}
          </span>
          <h1 className="mt-5 text-balance text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {HERO.title}
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted">{HERO.subtitle}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <PrimaryCta onClick={() => signin()}>
              {HERO.primaryCta}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">
                <path d="M4.5 12h15m0 0-6.75-6.75M19.5 12l-6.75 6.75" />
              </svg>
            </PrimaryCta>
            <SecondaryCta onClick={() => signup()}>{HERO.secondaryCta}</SecondaryCta>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
            {HERO.chips.map((chip) => (
              <li key={chip} className="inline-flex items-center gap-1.5 text-sm text-muted">
                <Icon name="check" className="h-4 w-4 text-emerald-500" strokeWidth={2.2} />
                {chip}
              </li>
            ))}
          </ul>

          <p className="mt-6 inline-flex items-start gap-2 text-sm text-muted">
            <Icon name="shield" className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={1.9} />
            <span>{HERO.note}</span>
          </p>
        </div>

        {/* Product-frame visual */}
        <div className="relative">
          <HeroProductFrame />
        </div>
      </div>
    </section>
  );
}

/** A mocked application window rendered entirely in CSS/SVG (no screenshot). */
function HeroProductFrame() {
  return (
    <div className="relative mx-auto w-full max-w-xl">
      {/* Glow behind the frame */}
      <div aria-hidden="true" className="absolute -inset-4 -z-10 rounded-token-xl bg-gradient-to-tr from-indigo-500/25 to-violet-500/25 blur-2xl" />

      <div className="overflow-hidden rounded-token-xl border border-border bg-surface shadow-elevation-4">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 border-b border-border bg-surface-2/60 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-rose-400" />
          <span className="h-3 w-3 rounded-full bg-amber-400" />
          <span className="h-3 w-3 rounded-full bg-emerald-400" />
          <span className="ml-3 hidden truncate rounded-full bg-surface px-3 py-1 text-xs text-muted sm:inline">
            app.karexia.com/tableau-de-bord
          </span>
        </div>

        {/* Mocked dashboard body */}
        <div className="grid grid-cols-3 gap-3 p-4">
          {/* KPI tiles */}
          {[
            { g: 'from-indigo-500 to-violet-500', w: 'w-10' },
            { g: 'from-sky-500 to-indigo-500', w: 'w-12' },
            { g: 'from-emerald-500 to-teal-500', w: 'w-8' },
          ].map((tile, i) => (
            <div key={i} className="rounded-token border border-border bg-surface-2/50 p-3">
              <span className={`mb-2 block h-7 w-7 rounded-token bg-gradient-to-br ${tile.g}`} />
              <span className={`mb-1.5 block h-2.5 ${tile.w} rounded-full bg-foreground/70`} />
              <span className="block h-2 w-8 rounded-full bg-muted/40" />
            </div>
          ))}

          {/* Chart card */}
          <div className="col-span-2 rounded-token border border-border bg-surface-2/40 p-3">
            <span className="mb-3 block h-2.5 w-24 rounded-full bg-muted/50" />
            <svg viewBox="0 0 200 80" className="h-24 w-full" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="hero-spark" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(124 58 237 / 0.35)" />
                  <stop offset="100%" stopColor="rgb(124 58 237 / 0)" />
                </linearGradient>
              </defs>
              <path d="M0 60 L28 48 L56 54 L84 32 L112 40 L140 20 L168 26 L200 8 L200 80 L0 80 Z" fill="url(#hero-spark)" />
              <path d="M0 60 L28 48 L56 54 L84 32 L112 40 L140 20 L168 26 L200 8" fill="none" stroke="rgb(124 58 237)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Activity list card */}
          <div className="rounded-token border border-border bg-surface-2/40 p-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="mb-2.5 flex items-center gap-2 last:mb-0">
                <span className="h-5 w-5 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500" />
                <span className="block h-2 flex-1 rounded-full bg-muted/40" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating "IA" chip for depth */}
      <div className="absolute -bottom-5 -left-5 hidden items-center gap-2 rounded-token-lg border border-border bg-surface px-3 py-2 shadow-elevation-3 sm:flex">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-token bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
          <Icon name="robot" className="h-5 w-5" strokeWidth={1.9} />
        </span>
        <div className="text-xs">
          <p className="font-semibold text-foreground">Équipe IA</p>
          <p className="text-muted">au travail 24/7</p>
        </div>
      </div>
    </div>
  );
}
